import { useState } from 'react';
import { useInfiniteQuery, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@pc/api/client';
import type { operations } from '@pc/api/schema';
import type { Tone } from './tone';

/**
 * The one shape every async job comes back as, regardless of `kind`. Derived
 * from the contract rather than restated, so a schema change is a type error
 * here instead of a silent mismatch.
 */
export type JobRecord = operations['getJob']['responses'][200]['content']['application/json'];
export type JobState = JobRecord['state'];

/**
 * One page of `listJobs`, and the rows on it.
 *
 * Derived separately from `JobRecord` rather than borrowed from it, because the
 * two reads are genuinely different shapes: a list row carries `targetType` and
 * `targetId`, which the single-job read does not, and carries neither `result`
 * nor `error`, which it does. Reusing one for the other would describe fields
 * that never arrive.
 */
export type JobPage = operations['listJobs']['responses'][200]['content']['application/json'];
export type JobSummary = JobPage['records'][number];
export type JobKind = JobSummary['kind'];

/**
 * Every filter `listJobs` declares, minus the two that are paging plumbing
 * rather than something an operator states. `cursor` belongs to the query hook
 * below, and `limit` is a page size this screen has no control for — it pages
 * with a button and takes the contract's default of 50.
 */
export type JobFilters = Omit<
  NonNullable<operations['listJobs']['parameters']['query']>,
  'cursor' | 'limit'
>;

/**
 * `queued` and `running` are the only states a job moves out of on its own.
 * The other three are terminal — nothing server-side changes them further,
 * so polling past that point would just spend rate-limit budget to keep
 * being told the same thing.
 */
const TERMINAL_STATES: ReadonlySet<JobState> = new Set(['succeeded', 'failed', 'abandoned']);

export function isTerminalJobState(state: JobState): boolean {
  return TERMINAL_STATES.has(state);
}

/**
 * The one place a job state becomes a colour. `abandoned` is deliberately its
 * own tone rather than `bad`: the service restarted mid-run, the job is never
 * resumed automatically (re-running provisioning could duplicate real
 * infrastructure), and the failure red would bury the one state that actually
 * needs a human to go look. See `--abandoned` in app.css and `Tone` in ui.tsx.
 */
export function toneForJobState(state: JobState): Tone {
  switch (state) {
    case 'queued':
      return 'neutral';
    case 'running':
      return 'info';
    case 'succeeded':
      return 'good';
    case 'failed':
      return 'bad';
    case 'abandoned':
      return 'abandoned';
  }
}

export function shortJobId(jobId: string): string {
  return jobId.slice(0, 8);
}

/**
 * The two enumerated filters, as the option lists a `<select>` needs.
 *
 * Both are enumerated in the contract, so the list is the contract's own
 * rather than a guess. This job list's `targetType` is the counter-example and
 * stays text — see `TargetCell` in `JobsBrowser` — but note it is now the only
 * one of its kind: the audit trail's `targetType` was a bare string when this
 * console was built and has since been given a real enum upstream.
 *
 * `satisfies` ties each list back to the contract's own union, so a value
 * renamed or dropped upstream fails to compile here rather than becoming a
 * filter that silently matches nothing. A value *added* upstream is caught one
 * function up: `toneForJobState` has no `default`, so a state it does not name
 * stops the file compiling.
 */
export const JOB_STATES = [
  'queued',
  'running',
  'succeeded',
  'failed',
  'abandoned',
] as const satisfies readonly JobState[];

export const JOB_KINDS = [
  'create-lxc',
  'delete-lxc',
  'delete-account',
] as const satisfies readonly JobKind[];

/*
 * Poll cadence.
 *
 * The rate limit is 120 requests/min per actor, and the handoff is explicit
 * that jobs must not be polled aggressively. Provisioning and teardown run
 * for minutes — a create-lxc job touches Proxmox, pfSense, Warpgate and
 * inventory state in sequence — so a sub-second interval cannot make a job
 * finish any sooner, it only burns quota an operator may need for something
 * else in the same minute (e.g. having several jobs open in different tabs).
 *
 * So: start responsive (2s), so a job that finishes quickly is not left
 * looking stuck, and ease linearly out to 10s over two minutes of runtime,
 * since a job still going after that long is provisioning something real and
 * checking in every couple of seconds buys nothing but load.
 */
const MIN_POLL_MS = 2_000;
const MAX_POLL_MS = 10_000;
const POLL_EASE_WINDOW_MS = 2 * 60_000;

function pollIntervalForRunningJob(job: JobRecord): number {
  // `startedAt` is null while still `queued`; fall back to `createdAt` so the
  // easing clock starts at submission rather than reporting 0 elapsed time
  // for the entire queued phase.
  const reference = job.startedAt ?? job.createdAt;
  const elapsedMs = Math.max(0, Date.now() - Date.parse(reference));
  const eased = Math.min(1, elapsedMs / POLL_EASE_WINDOW_MS);
  return Math.round(MIN_POLL_MS + eased * (MAX_POLL_MS - MIN_POLL_MS));
}

/**
 * `refetchInterval` callback: `false` stops polling outright once a job is
 * terminal, otherwise it returns the eased interval above. Exported on its
 * own so the stop condition and the schedule are each defined in exactly one
 * place rather than re-derived at the call site.
 *
 * It answers for a job that exists. Whether a job id resolves at all is the
 * caller's question — see `useJob`, which stops polling an id that has never
 * once read successfully.
 */
export function jobPollIntervalMs(job: JobRecord | undefined): number | false {
  // No successful read yet — e.g. the first fetch failed and the shared retry
  // policy in `lib/query.ts` has exhausted its attempts. Keep checking, but no
  // more eagerly than the eased ceiling; there is no job state yet to time the
  // easing against.
  if (!job) return MAX_POLL_MS;
  if (isTerminalJobState(job.state)) return false;
  return pollIntervalForRunningJob(job);
}

/**
 * Reads one job and polls it while it is in flight.
 *
 * Deliberately does not set `retry` or `retryDelay`: the shared client policy
 * in `lib/query.ts` already declines to retry 403/404 (real answers that will
 * not change) and honours `Retry-After` on 429, and duplicating that logic
 * here would just risk it drifting out of sync.
 */
export function useJob(jobId: string | null): UseQueryResult<JobRecord> {
  return useQuery({
    // `enabled` rather than a conditional hook: a caller that has not started
    // a job yet still has to call this unconditionally.
    enabled: jobId !== null && jobId !== '',
    queryKey: ['jobs', jobId],
    queryFn: async (): Promise<JobRecord> => {
      const { data } = await api.GET('/api/jobs/{jobId}', {
        params: { path: { jobId: jobId as string } },
      });
      return data as JobRecord;
    },
    /*
     * A read that has never once succeeded is not a job being watched, it is a
     * job id that does not resolve — a truncated one in a shared link, or one
     * the API has forgotten. `jobPollIntervalMs` returns the ceiling when there
     * is no data, and the interval survives the error state, so that used to be
     * six requests a minute forever against a 120/min budget, per stale tab.
     *
     * The condition is deliberately narrow, and matches `useAsyncOperation`'s
     * `unreadable` so the two cannot disagree: only *never* read plus errored
     * stops. One failed refetch of a job we have seen running keeps the watch,
     * which is the case that matters during a teardown.
     */
    refetchInterval: (query) =>
      query.state.data === undefined && query.state.status === 'error'
        ? false
        : jobPollIntervalMs(query.state.data),
  });
}

/* ------------------------------------------------------------------- list */

/** One request for one page of the list. `cursor` absent means the newest page. */
async function fetchJobPage(filters: JobFilters, cursor: string | undefined): Promise<JobPage> {
  const { data } = await api.GET('/api/jobs', {
    params: {
      query: {
        ...filters,
        // An absent key, not an explicit undefined: `exactOptionalPropertyTypes`
        // makes those different types, and only the first leaves the parameter
        // off the query string. `cursor=` is a 400, not "no cursor".
        ...(cursor === undefined ? {} : { cursor }),
      },
    },
  });
  return data as JobPage;
}

/**
 * Whether the newest page is still worth re-reading, and how often.
 *
 * `useJob`'s 2s→10s ease is budgeted for one row against 120 requests/min. A
 * page is fifty, so this holds flat at that ease's *ceiling* — six requests a
 * minute keeps a provisioning watch live without competing with the operator's
 * own work for quota, and a list is a watch rather than a countdown: nobody is
 * waiting on this screen for a single job to tick over.
 *
 * The stop condition is the same one `useJob` uses, asked of every row instead
 * of one: nothing server-side moves a terminal job, so a page with no live work
 * on it would only be re-read to be told the same thing. That falls out neatly
 * under a `state` filter too — `?state=succeeded` polls not at all, because
 * every row it can ever return is already finished.
 *
 * No data at all — the first read failed and the shared retry policy gave up —
 * stops rather than backs off. There is no evidence of work in flight to watch,
 * and `ApiErrorNotice` puts a retry in the operator's hands.
 */
export function jobListPollIntervalMs(page: JobPage | undefined): number | false {
  if (!page) return false;
  return page.records.some((record) => !isTerminalJobState(record.state)) ? MAX_POLL_MS : false;
}

/**
 * The newest page is re-read on a timer; the history below it is not, so the
 * two halves are allowed to disagree. Under `?state=running` a job that
 * finishes drops out of the newest page, everything behind it shifts up, and a
 * row already sitting in a loaded history page reappears at the top. Keeping
 * the first occurrence keeps the fresher copy of that row and stops it being
 * rendered twice under one React key.
 */
function dedupeByJobId(records: JobSummary[]): JobSummary[] {
  const seen = new Set<string>();
  const unique: JobSummary[] = [];
  for (const record of records) {
    if (seen.has(record.jobId)) continue;
    seen.add(record.jobId);
    unique.push(record);
  }
  return unique;
}

/** What a jobs list needs from its data, with the paging plumbing hidden. */
export interface JobListView {
  /** Every row loaded so far: the newest page, then whatever history follows it. */
  records: JobSummary[];
  /** The first read of the newest page has not answered yet. */
  isPending: boolean;
  /**
   * Something failed. Separate from `records.length === 0` on purpose: an empty
   * list and a list that could not be read are different statements, and only
   * the first of them is a fact about how many jobs exist.
   */
  isError: boolean;
  error: unknown;
  retry: () => void;
  /** A strictly later page exists — from `nextCursor`, never from a page's size. */
  hasMore: boolean;
  loadMore: () => void;
  isLoadingMore: boolean;
  /** The newest page is being re-read, because it still holds work in flight. */
  watching: boolean;
}

/**
 * The jobs list: one live page, and as much history behind it as was asked for.
 *
 * Two queries rather than the single `useInfiniteQuery` that `AccountsBrowser`
 * and `ContainersBrowser` use, for one reason: refetching an infinite query
 * refetches *every page it has loaded*, one request each (see
 * `infiniteQueryBehavior` in query-core — a refetch replays `pageParams` in
 * order). Polled, that would make watching a provisioning run cost more the
 * further back an operator had paged, which is exactly backwards — history does
 * not change, and the thing worth watching is the top of the list, where new
 * jobs appear. So the newest page gets its own plain query and its own timer,
 * and the history behind it is a separate infinite query that is fetched when
 * asked for and then left alone.
 *
 * The history is anchored to the cursor the newest page carried at the moment
 * the operator first asked for more, held in state rather than re-read from the
 * polled data. Re-deriving it would move the anchor every time a job was
 * created, which would re-key the query and throw away the pages already
 * loaded — reshuffling the history someone is reading because something
 * unrelated started at the top.
 *
 * Neither query sets `retry` or `retryDelay`; the shared policy in
 * `lib/query.ts` already declines to retry answers that will not change and
 * honours `Retry-After`, and a second copy of that here would only drift.
 *
 * Filter changes are not handled here: the caller remounts this hook's owner
 * when the committed filters change (see `routes/jobs.tsx`), which resets the
 * anchor along with the form drafts. Carrying a cursor across a filter change
 * would page through the previous result set under the new filter.
 */
export function useJobList(filters: JobFilters): JobListView {
  const newest = useQuery({
    queryKey: ['jobs', 'list', filters],
    queryFn: () => fetchJobPage(filters, undefined),
    refetchInterval: (query) => jobListPollIntervalMs(query.state.data),
  });

  const [historyFrom, setHistoryFrom] = useState<string | null>(null);

  const history = useInfiniteQuery({
    // Nothing to fetch until the operator asks for history, which is also what
    // makes the `initialPageParam` fallback below unreachable.
    enabled: historyFrom !== null,
    queryKey: ['jobs', 'list', filters, 'from', historyFrom],
    initialPageParam: historyFrom ?? '',
    queryFn: ({ pageParam }) => fetchJobPage(filters, pageParam),
    // The cursor is the only stop condition. `nextCursor` is documented as
    // present only when a strictly later page exists, so its absence — not an
    // empty page, not a short page — is what ends paging.
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const records = dedupeByJobId([
    ...(newest.data?.records ?? []),
    ...(history.data?.pages.flatMap((page) => page.records) ?? []),
  ]);

  return {
    records,
    isPending: newest.isPending,
    isError: newest.isError || history.isError,
    error: newest.error ?? history.error,
    retry: () => {
      if (newest.isError) void newest.refetch();
      if (history.isError) void history.refetch();
    },
    // Before the first "load more" the newest page owns the cursor; afterwards
    // the history query does, and its own `nextCursor` is the only thing that
    // can say whether there is anything older still.
    hasMore: historyFrom === null ? newest.data?.nextCursor !== undefined : history.hasNextPage,
    loadMore: () => {
      if (historyFrom !== null) {
        void history.fetchNextPage();
        return;
      }
      const cursor = newest.data?.nextCursor;
      if (cursor !== undefined) setHistoryFrom(cursor);
    },
    isLoadingMore: history.isFetching,
    watching: jobListPollIntervalMs(newest.data) !== false,
  };
}
